<template>
    <div ref="autocompleteList" class="label-autocomplete-list scrollbar">
        <section v-for="group in groupedNodes" :key="group.id" class="label-autocomplete-list__group">
            <span class="title">{{ group.node.layout.caption }}</span>
            <div class="children">
                <div v-for="child in group.children" :key="child.id" class="children__item" :class="{
                    'children__item--layer': child.node.layout.type === gConsts.node.layout.types.LAYER,
                    'children__item--label': child.node.layout.type === gConsts.node.layout.types.LABEL
                }">
                    <template v-if="child.node.layout.type === gConsts.node.layout.types.LAYER">
                        <span class="title">{{ child.node.layout.caption }}</span>
                        <section class="children">
                            <labelAutocompleteOption v-for="label in child.children" :key="label.id"
                                @click="selectNode(label.node)" :labelNode="label.node" :searchText="searchText"
                                :focused="cursor.labelId === label.id" :insertedOnReport="reportNodeIds.has(label.node.id)" />
                        </section>
                    </template>
                    <template v-if="child.node.layout.type === gConsts.node.layout.types.LABEL">
                        <labelAutocompleteOption :labelNode="child.node" @click="selectNode(child.node)"
                            :searchText="searchText" :focused="cursor.labelId === child.id" :insertedOnReport="reportNodeIds.has(child.node.id)" />
                    </template>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { evtBus } from '@/main';
import { gConsts } from '@/globals/gConsts';
import { reportUtils } from '@/features/report/lib/reportUtils';
import { mapGetters } from 'vuex';
import labelAutocompleteOption from './labelAutocompleteOption.vue';

export default {
    name: 'labelAutocompleteList',
    components: {
        labelAutocompleteOption
    },
    props: {
        searchText: {
            type: String,
            default: ''
        },
        filterLayerId: {
            type: String,
            default: null
        },
        filterReportNodes: {
            type: Boolean,
            default: false
        },
        cursorActive: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            gConsts: gConsts,
            cursor: {
                position: 0,
                labelId: null
            }
        }
    },

    computed: {
        ...mapGetters([
            'GET_nodesArray',
            'GET_reportNodes'
        ]),

        reportNodeIds() {
            return new Set(this.GET_reportNodes.map(reportNode => reportNode.id));
        },

        labelNodes() {
            if (!this.GET_nodesArray)
                return [];
            return this.GET_nodesArray.filter(node =>
                reportUtils.nodeHasReportLine(node) &&
                (this.filterLayerId == null || node.layout.parent === this.filterLayerId) &&
                (this.filterReportNodes == false || !this.reportNodeIds.has(node.id))
            );
        },

        filteredNodes() {
            return this.labelNodes.filter(node => !this.searchText || reportUtils.labelSearchForReportMatches(node, this.searchText));
        },

        groupedNodes() {
            // Agrupo por step -> layer -> labels
            return this.filteredNodes.reduce((acc, label) => {
                const groupInfo = this.getGroupInfoByNode(label);
                if (!groupInfo)
                    return acc;

                const { step, layer } = groupInfo;
                let stepGroup = acc.find(groupItem => groupItem.id === step.id);
                if (!stepGroup) {
                    stepGroup = {
                        id: step.id,
                        node: step,
                        children: []
                    };
                    acc.push(stepGroup);
                }
                if (!layer) {
                    stepGroup.children.push({
                        id: label.id,
                        node: label,
                    });
                    return acc;
                }

                const layerGroup = stepGroup.children.find(child => child.id === layer.id);
                if (layerGroup) {
                    layerGroup.children.push({
                        id: label.id,
                        node: label,
                    });
                } else {
                    stepGroup.children.push({
                        id: layer.id,
                        node: layer,
                        children: [{
                            id: label.id,
                            node: label,
                        }]
                    });
                }
                return acc;
            }, []);
        },
    },

    watch: {
        cursorActive(newVal) {
            if (!newVal) {
                this.resetCurrentCursor();
            }
        },
        searchText() {
            this.resetCurrentCursor();
        },
        filteredNodes: {
            handler(newVal) {
                this.$emit('onTotalResultsChange', { search: newVal.length, total: this.labelNodes.length });
            },
            immediate: true
        },
    },

    methods: {

        getGroupInfoByNode(labelNode) {
            const parentNode = this.getParentNodeById(labelNode.layout.parent);
            if (!parentNode)
                return null;

            if (parentNode.layout.type === gConsts.node.layout.types.STEP) {
                return {
                    step: parentNode,
                    layer: null
                };
            } else if (parentNode.layout.type === gConsts.node.layout.types.LAYER) {
                return {
                    layer: parentNode,
                    step: this.getParentNodeById(parentNode.layout.parent)
                };
            }
            return null;
        },

        getParentNodeById(nodeId) {
            return this.GET_nodesArray.find(node => node.id === nodeId);
        },

        selectNode(node) {
            this.$emit('select', node);
        },

        selectCurrentLabel() {
            if (!this.cursorActive)
                return false;
            if (this.cursor.labelId) {
                this.selectNode(this.filteredNodes.find(node => node.id === this.cursor.labelId));
                return true;
            }
            return false;
        },

        moveCursor(direction) {
            if (!this.cursorActive)
                return;
            if (direction == 'next') {
                this.cursor.position++;
            } else if (direction == 'previous') {
                this.cursor.position--;
            }
            if (this.cursor.position < 0) {
                this.cursor.position = this.filteredNodes.length - 1;
            } else if (this.cursor.position >= this.filteredNodes.length) {
                this.cursor.position = 0;
            }
            this.refreshCurrentCursorLabel();
        },

        resetCurrentCursor() {
            this.cursor.position = 0;
            this.cursor.labelId = null;
            this.refreshCurrentCursorLabel();
        },

        refreshCurrentCursorLabel() {
            this.cursor.labelId = this.filteredNodes[this.cursor.position]?.id ?? null;
        },
    },

    mounted() {

        evtBus.$on('evt_labelAutocomplete_action', (action, payload) => {
            // console.log('evt_pesquisaAnotacoes_action: ', action, payload);
            switch (action) {
                case 'cursorMove':
                    this.moveCursor(payload.direction);
                    break;
                case 'cursorSelectTerm':
                    this.selectCurrentLabel();
                    break;
            }
        });
    }
}
</script>

<style scoped>
.label-autocomplete-list {
    background: white;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    max-height: 300px;
    overflow-y: auto;
    text-align: left;
    animation: fadeIn 0.3s ease-in-out;
}

.label-autocomplete-list__group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
}

.label-autocomplete-list__group>.title {
    color: var(--color-primary);
    font-weight: 700;
    font-size: var(--text-sm);
    text-transform: uppercase;
    letter-spacing: 0.48px;
}

.label-autocomplete-list__group>.children {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
}

.children__item--layer {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
}

.children__item--layer>.title {
    color: var(--color-dark-gray);
    font-size: var(--text-sm);
    font-weight: 600;
    letter-spacing: 0.28px;
    border-left: 2px solid var(--border-primary);
    padding-left: var(--spacing-md);
}

.children__item--layer>.children {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    padding-left: var(--spacing-sm);
}
</style>
